/**
 * Shared time constants for `apps/web`.
 *
 * PATTERN TO FOLLOW: any cache, debounce, or timeout duration used
 * in more than one place (or representing a product decision, not a local
 * detail) lives here, not as a loose `const` inside a component.
 *
 * Why: these values were redeclared in ~12 files with two spellings of the same
 * number (`5 * 60 * 1000` and `300_000`), so changing the cache policy required
 * touching every site and risked leaving values divergent. Import from here
 * instead of redefining.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;

/** Milliseconds in a minute. Base for deriving all other durations. */
const MINUTE_MS = MS_PER_SECOND * SECONDS_PER_MINUTE;

/** Milliseconds in an hour. Used by foreground reminders window.*/
export const ONE_HOUR_MS = MINUTES_PER_HOUR * MINUTE_MS;

/** Minutes a cached datum is considered fresh (offline cache and session). */
const STALE_MINUTES = 5;

/**
 * Default `staleTime` for offline-first queries (PowerSync + TanStack).
 *
 * Data syncs locally through PowerSync, so a generous `staleTime` (5 min)
 * avoids redundant refetches/re-renders with no real risk of showing stale data.
 * It is also the global `queryClient` default.
 */
export const OFFLINE_CACHE_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * `staleTime` for session/authentication queries.
 *
 * It remains a separate constant (although it currently matches the offline
 * cache value) because it is an independent decision: it must stay aligned
 * across `useSession`, the `_app` route guard, and the `session-bridge`.
 */
export const SESSION_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * Ceiling for waiting on a `get-session` made INSIDE a route `beforeLoad`.
 *
 * P23/CC-60 · I1-bis: a `beforeLoad` that `await`s the network without a ceiling does not fail
 * with 401 or 503 (both resolve fast) — fails when response
 * STOPS: router does not mount route, `<Suspense fallback={null}>` of
 * `__root` has nothing to render and whole app goes BLANK for
 * however long the network takes. Measured on `/login`: empty `innerText` at 3, 6,
 * 10, 15, and 20 s on production bundle.
 *
 * SINGLE source for both route guards (`_app` and `_auth`): having two
 * literals was exactly what left `_auth` without the defense that `_app` had.
 * Once ceiling expires screen paints and late response continues
 * syncing machine/cache — `BootManager` redirects afterwards if
 * session was present.
 */
export const SESSION_CHECK_TIMEOUT_MS = 1500;

/**
 * Debounce for text inputs that persist while typing (title and notes in
 * detail, subtasks). Balances typing responsiveness with mutation volume.
 */
export const DEBOUNCE_DELAY_MS = 400;

/** Seconds before revoking a download's object URL (see constant). */
const OBJECT_URL_REVOKE_SECONDS = 10;

/**
 * Grace period before revoking a `blob:` object URL after triggering a download.
 *
 * It must be comfortably greater than the time the browser takes to consume
 * the `<a download>` href; revoking earlier would abort the download.
 */
export const OBJECT_URL_REVOKE_DELAY_MS = OBJECT_URL_REVOKE_SECONDS * MS_PER_SECOND;
